// NEW_FILE_JUSTIFIED: InsuranceRequestsPanel is the tabs container for all lead insurance requests on the lead detail page — no overlap with existing components
import { useState } from 'react';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official client-side React bindings package, not a server module
import { format } from 'date-fns';
import { FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { api } from '@convex/_generated/api';
import { useLeadInsuranceRequests } from '@/hooks/useLeadInsuranceRequests';
import { RequestTabBody } from './RequestTabBody';
import type {
  IInsuranceRequestsPanelProps,
  ITabTriggerForRequestProps,
} from '@/@types/leadInsuranceRequest';

export type { IInsuranceRequestsPanelProps };

export function InsuranceRequestsPanel({ leadId }: IInsuranceRequestsPanelProps) {
  const { requests, isLoading } = useLeadInsuranceRequests(leadId);

  const sorted = requests ? [...requests].sort((a, b) => b.submittedAt - a.submittedAt) : [];

  // useState MUST be before any early return (Rules of Hooks)
  const [selectedTab, setSelectedTab] = useState<string>('');

  // Requests arrive after the first render, so the initial state can never name
  // a real tab — derive the active tab and fall back to the newest request.
  const activeTab = sorted.some(r => r._id === selectedTab) ? selectedTab : (sorted[0]?._id ?? '');

  if (isLoading) {
    return <Skeleton className="h-72 w-full" />;
  }
  if (!requests || requests.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-primary" />
          Insurance requests
          <Badge variant="secondary">{sorted.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setSelectedTab}>
          <TabsList className="mb-3 h-auto flex-wrap gap-1">
            {sorted.map(r => (
              <TabTriggerForRequest key={r._id} request={r} />
            ))}
          </TabsList>
          {sorted.map(r => (
            <TabsContent key={r._id} value={r._id}>
              <RequestTabBody request={r} />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function TabTriggerForRequest({ request }: ITabTriggerForRequestProps) {
  const type = useQuery(api.insurance.types.getOrgType, {
    typeId: request.insuranceTypeId,
  });
  const provider = useQuery(
    api.insurance.providers.get,
    request.providerId ? { providerId: request.providerId } : 'skip'
  );

  const dateLabel = format(request.submittedAt, 'MMM d');
  const typeName = type?.name ?? '...';
  const providerLabel = provider?.name ?? 'No provider';

  return (
    <TabsTrigger value={request._id} className="gap-1.5">
      <span>{typeName}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">{dateLabel}</span>
      <Badge variant="outline" className="ml-1 text-xs">
        {providerLabel}
      </Badge>
    </TabsTrigger>
  );
}
